// A short two-tone chime for "something new arrived" on an operational screen.
// Synthesised with WebAudio (no asset to ship or serve). Browsers keep audio
// locked until the page has had a user gesture, so `unlockAudio` is called from
// the terminal overlay's Start button and from the first tap anywhere; until
// then `playChime` is a silent no-op rather than an error.

let ctx: AudioContext | null = null
let armed = false

function audioCtx(): AudioContext | null {
  if (typeof window === 'undefined') return null
  if (ctx) return ctx
  const Ctor = (window as unknown as { AudioContext?: typeof AudioContext; webkitAudioContext?: typeof AudioContext }).AudioContext
    || (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext
  if (!Ctor) return null
  try { ctx = new Ctor() } catch { ctx = null }
  return ctx
}

/** Resume audio from inside a user gesture. Safe to call repeatedly. */
export function unlockAudio(): void {
  const c = audioCtx()
  if (c && c.state === 'suspended') void c.resume().catch(() => { /* still locked */ })
}

/** Arm `unlockAudio` on the first pointer/key gesture anywhere on the page. */
export function armAudioOnFirstGesture(): void {
  if (armed || typeof document === 'undefined') return
  armed = true
  const once = () => {
    unlockAudio()
    document.removeEventListener('pointerdown', once, true)
    document.removeEventListener('keydown', once, true)
  }
  document.addEventListener('pointerdown', once, true)
  document.addEventListener('keydown', once, true)
}

/** Play the chime (no-op while audio is locked or unsupported). */
export function playChime(): void {
  const c = audioCtx()
  if (!c || c.state !== 'running') return
  try {
    const t0 = c.currentTime
    for (const [i, freq] of [880, 1320].entries()) {
      const osc = c.createOscillator()
      const gain = c.createGain()
      osc.type = 'sine'
      osc.frequency.value = freq
      const start = t0 + i * 0.16
      gain.gain.setValueAtTime(0.0001, start)
      gain.gain.exponentialRampToValueAtTime(0.25, start + 0.02)
      gain.gain.exponentialRampToValueAtTime(0.0001, start + 0.28)
      osc.connect(gain).connect(c.destination)
      osc.start(start)
      osc.stop(start + 0.3)
    }
  } catch { /* audio is a nicety; never break the page for it */ }
}
